import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash';
import cs from 'classnames';

import usePanel from '@tcs/common/hooks/usePanel';
import { useNavigate } from 'react-router-dom';
import { SearchInput } from '@forgeglobal/design-framework';
import useRecentlyViewedAccounts from '@tcs/client-portal-common/hooks/useRecentlyViewedAccounts';
import { Persona } from '@tcs/client-portal-api/types/Persona';
import Panel from './Panel';
import NoResultsPanel from './NoResultsPanel';
import LoadingPanel from './LoadingPanel';
import { useStyles } from './style';
import { SearchInputType, SearchProps } from './types';

const PLACEHOLDERS: Record<SearchInputType, string> = {
  representative: 'Search for name or account number',
  admin: 'Search for name, account number, or representative code',
};

const PERSONA_PATHS = {
  [Persona.Individual]: 'accounts',
  [Persona.Representative]: 'representatives',
};

const Search = ({
  items,
  onSearch,
  savedKey,
  loading,
  type,
  inputClassName,
}: SearchProps) => {
  const classes = useStyles();
  const nav = useNavigate();
  const [searchString, setSearchString] = useState('');
  const { recentItems } = useRecentlyViewedAccounts(savedKey);
  const ref = useRef(null);
  const [openPanel, setOpenPanel] = usePanel(ref);
  const shouldShowRecentViewed = !searchString;

  const delayedSearch = useMemo(
    () => debounce((value: string) => onSearch(value), 500),
    [onSearch],
  );

  const handleChange = (event) => {
    const value = event.target.value.replace(/[^\w ]/g, '');
    setSearchString(value);
    setOpenPanel(true);
  };

  useEffect(() => {
    if (searchString) delayedSearch(searchString);

    return delayedSearch.cancel;
  }, [searchString, delayedSearch]);

  const handleFocus = () => {
    if (recentItems) {
      setOpenPanel(true);
    }
  };

  const handleClear = () => {
    setSearchString('');
    if (!recentItems) {
      setOpenPanel(false);
    }
  };

  const handleItemSelect = (account, persona) => () => {
    setSearchString('');
    setOpenPanel(false);

    if (account.isExistingAccount === false && account.applicationId) {
      nav(`/applications/${account.applicationId}`, {
        state: { searchValue: searchString },
      });
      return;
    }

    nav(`/${PERSONA_PATHS[persona]}/${account.id}`);
  };

  const handlePanelClose = () => {
    setOpenPanel(false);
  };

  const getCategorizedItems = useCallback((list) => {
    if (!list) return [];
    const categories = [
      ...new Set(list.flatMap((d) => d.data).map((item) => item.persona)),
    ];
    const result = {};
    if (!categories.length) return result;
    categories.forEach((category: string) => {
      result[category] = list.filter((page) =>
        page.data.some((d) => d.persona === category),
      )[0];
    });
    return result;
  }, []);

  const getRecentItemsPage = useCallback(() => {
    const recentItemsPage = {};

    for (const key in recentItems) {
      const items = recentItems[key];
      recentItemsPage[key] = {
        data: items,
        count: items.length,
      };
    }

    return recentItemsPage;
  }, [recentItems]);

  const filteredItems = useMemo(
    () =>
      shouldShowRecentViewed
        ? getRecentItemsPage()
        : getCategorizedItems(items),
    [shouldShowRecentViewed, items, getRecentItemsPage, getCategorizedItems],
  );

  const renderPanelContent = () => {
    if (loading) {
      return <LoadingPanel />;
    } else if (
      searchString &&
      (!items || !items.flatMap((p) => p.data).length)
    ) {
      return <NoResultsPanel type={type} />;
    } else {
      return (
        <Panel
          searchedValue={searchString}
          isRecent={shouldShowRecentViewed}
          onSelect={handleItemSelect}
          onClose={handlePanelClose}
          onSearch={setSearchString}
          items={filteredItems}
        />
      );
    }
  };

  return (
    <div className={classes.wrapper} ref={ref}>
      <SearchInput
        placeholder={PLACEHOLDERS[type]}
        value={searchString}
        onChange={handleChange}
        onFocus={handleFocus}
        onClear={handleClear}
        className={cs(classes.input, inputClassName)}
      />
      {openPanel && <div className={classes.panel}>{renderPanelContent()}</div>}
    </div>
  );
};

export default Search;
